<template>
	<!-- QR Code Modal -->
	<div v-if="selectedQRWireguard" class="qr-modal" @click="closeQRCode">
		<qrcode-vue :margin="10" :value="selectedQRWireguard.config" :size="400" render-as="svg" />
	</div>
	
	<UserPageLayout :loading="loading" :error="error">
		<table class="generic-table">
			<thead>
				<tr>
					<th>Tag</th>
					<th>Status</th>
					<th>C2C</th>
					<th>Created</th>
					<th>Actions</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="wireguard in wireguards" :key="wireguard.tag">
					<td>{{ wireguard.tag }}</td>
					<td>{{ wireguard.status }}</td>
					<td>
						<input 
							type="checkbox" 
							:checked="wireguard.c2c_enabled"
							disabled
							style="cursor: not-allowed;"
						/>
					</td>
					<td>{{ $formatDate(wireguard.created) }}</td>
					<td>
						<div class="generic-table-actions">
							<button 
								@click="downloadConfig(wireguard)"
								class="generic-table-action-button"
								:disabled="action_loading"
							>
								Config
							</button>
							<button 
								@click="downloadQRCode(wireguard)"
								class="generic-table-action-button"
								:disabled="action_loading"
							>
								QR
							</button>
							<button 
								v-if="!wireguard.c2c_enabled"
								@click="enableC2C(wireguard.ipv4)"
								class="generic-table-action-button"
								:disabled="action_loading"
							>
								Enable C2C
							</button>
							<button 
								v-if="wireguard.c2c_enabled"
								@click="disableC2C(wireguard.ipv4)"
								class="generic-table-action-button"
								:disabled="action_loading"
							>
								Disable C2C
							</button>
							<button 
								@click="deleteWireguard(wireguard.ipv4)"
								class="generic-table-action-button"
								:disabled="action_loading"
							>
								Delete
							</button>
						</div>
					</td>
				</tr>
				<tr>
					<td>
						<input 
							v-model="newTag" 
							type="text" 
							:placeholder="`Tag ${tagRegex} (${minTagLength}-${maxTagLength})`"
							:minlength="minTagLength"
							:maxlength="maxTagLength"
							@input="filterTagInput"
							@keyup.enter="addWireguard"
							:disabled="action_loading"
						/>
					</td>
					<td colspan="3"></td>
					<td>
						<button 
							@click="addWireguard"
							class="generic-table-action-button"
							:disabled="action_loading || !tagMeetsLengthRules"
						>
							Add
						</button>
					</td>
				</tr>
			</tbody>
		</table>
	</UserPageLayout>
</template>

<script>
import UserPageLayout from './_PageLayout.vue'
import QrcodeVue from 'qrcode.vue'

export default {
	name: 'UserWireguard',
	components: {
		UserPageLayout,
		QrcodeVue
	},
	data() {
		return {
			loading: true,
			action_loading: false,
			error: '',
			wireguards: [],
			newTag: '',
			tagRegex: '',
			minTagLength: 1,
			maxTagLength: 256,
			selectedQRWireguard: null
		}
	},
	computed: {
		tagMeetsLengthRules() {
			const len = this.newTag.trim().length
			return len >= this.minTagLength && len <= this.maxTagLength
		}
	},
	async mounted() {
		await this.fetchWireguards()
	},
	methods: {
		async fetchWireguards() {
			this.loading = true
			this.error = ''

			const authToken = localStorage.getItem('authToken')?.trim()
			if (!authToken || authToken === 'null') {
				this.$router.push({ name: 'Login' })
				return
			}
			
			try {
				const response = await this.$api.post('/api/user/wireguard', {
					authToken: authToken
				})
				
				this.wireguards = response.data.wireguards || []
				this.tagRegex = response.data.tag_regex
				this.minTagLength = response.data.min_tag_length
				this.maxTagLength = response.data.max_tag_length
			} catch (error) {
				if (error.response && error.response.status === this.$StatusCodes.UNAUTHORIZED) {
					localStorage.removeItem('authToken')
					this.$router.push({ name: 'Login' })
				} else {
					this.error = 'An error occurred while loading wireguard configurations.'
					console.error('Error:', error)
				}
			} finally {
				this.loading = false
			}
		},
		filterTagInput(event) {
			const newValue = event.target.value
			try {
				const regex = new RegExp(this.tagRegex, 'g')
				const match = newValue.match(regex)
				const validValue = match ? match.join('') : ''
				this.newTag = validValue
			} catch (e) {
				// If regex is invalid, don't filter
				this.newTag = newValue
			}
		},
		async addWireguard() {
			if (!this.newTag.trim()) {
				return
			}

			this.action_loading = true
			this.error = ''
			const authToken = localStorage.getItem('authToken')?.trim()

			try {
				await this.$api.post('/api/user/wireguard/add', {
					authToken: authToken,
					tag: this.newTag.trim()
				})
				this.newTag = ''
				await this.fetchWireguards()
			} catch (error) {
				if (error.response && error.response.status === this.$StatusCodes.UNAUTHORIZED) {
					localStorage.removeItem('authToken')
					this.$router.push({ name: 'Login' })
				} else if (error.response && error.response.status === this.$StatusCodes.BAD_REQUEST) {
					this.error = 'Maximum wireguard configurations reached.'
				} else if (error.response && error.response.status === this.$StatusCodes.TOO_MANY_REQUESTS) {
					this.error = 'No available IP addresses. Contact admin?'
				} else if (error.response && error.response.status === this.$StatusCodes.CONFLICT) {
					this.error = 'A wireguard configuration with this tag already exists.'
				} else {
					this.error = 'An error occurred while adding wireguard configuration.'
					console.error('Error:', error)
				}
			} finally {
				this.action_loading = false
			}
		},
		async deleteWireguard(ipv4) {
			this.action_loading = true
			this.error = ''
			const authToken = localStorage.getItem('authToken')?.trim()

			try {
				await this.$api.post('/api/user/wireguard/delete', {
					authToken: authToken,
					ipv4: ipv4
				})
				await this.fetchWireguards()
			} catch (error) {
				if (error.response && error.response.status === this.$StatusCodes.UNAUTHORIZED) {
					localStorage.removeItem('authToken')
					this.$router.push({ name: 'Login' })
				} else if (error.response && error.response.status === this.$StatusCodes.NOT_FOUND) {
					this.error = 'Wireguard configuration not found.'
				} else {
					this.error = 'An error occurred while deleting wireguard configuration.'
					console.error('Error:', error)
				}
			} finally {
				this.action_loading = false
			}
		},
		downloadConfig(wireguard) {
			if (!wireguard.config) {
				this.error = 'Configuration not available.'
				return
			}
			
			// Create a blob with the config content
			const blob = new Blob([wireguard.config], { type: 'text/plain' })
			const url = window.URL.createObjectURL(blob)
			const link = document.createElement('a')
			link.href = url
			link.download = `${wireguard.tag}.conf`
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			window.URL.revokeObjectURL(url)
		},
		downloadQRCode(wireguard) {
			if (!wireguard.config) {
				this.error = 'Configuration not available.'
				return
			}
			this.selectedQRWireguard = wireguard
		},
		closeQRCode() {
			this.selectedQRWireguard = null
		},
		async enableC2C(ipv4) {
			this.action_loading = true
			this.error = ''
			const authToken = localStorage.getItem('authToken')?.trim()

			try {
				await this.$api.post('/api/user/wireguard/c2c/enable', {
					authToken: authToken,
					ipv4: ipv4
				})
				await this.fetchWireguards()
			} catch (error) {
				if (error.response && error.response.status === this.$StatusCodes.UNAUTHORIZED) {
					localStorage.removeItem('authToken')
					this.$router.push({ name: 'Login' })
				} else if (error.response && error.response.status === this.$StatusCodes.NOT_FOUND) {
					this.error = 'Wireguard configuration not found.'
				} else {
					this.error = 'An error occurred while enabling C2C.'
					console.error('Error:', error)
				}
			} finally {
				this.action_loading = false
			}
		},
		async disableC2C(ipv4) {
			this.action_loading = true
			this.error = ''
			const authToken = localStorage.getItem('authToken')?.trim()

			try {
				await this.$api.post('/api/user/wireguard/c2c/disable', {
					authToken: authToken,
					ipv4: ipv4
				})
				await this.fetchWireguards()
			} catch (error) {
				if (error.response && error.response.status === this.$StatusCodes.UNAUTHORIZED) {
					localStorage.removeItem('authToken')
					this.$router.push({ name: 'Login' })
				} else if (error.response && error.response.status === this.$StatusCodes.NOT_FOUND) {
					this.error = 'Wireguard configuration not found.'
				} else {
					this.error = 'An error occurred while disabling C2C.'
					console.error('Error:', error)
				}
			} finally {
				this.action_loading = false
			}
		}
	}
}
</script>

<style scoped>
input {
	width: 100%;
	padding: 8px;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 14px;
	box-sizing: border-box;
	line-height: 1.5;
	min-width: 200px;
}

input::placeholder {
	font-weight: bold;
	font-size: 12px;
}

.qr-modal {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 10000;
	overflow: auto;
}

</style>
